import { forwardRef, useMemo } from 'react'
import { Link, type LinkProps } from 'react-router'

import { useLocale } from './LocaleProvider'
import { localizeUrl, stripLocaleFromPath } from './utils'


export interface LocalizedLinkProps extends Omit<LinkProps, 'to'> {
  to: string
  /** Skip locale prefix (for external links or special cases) */
  skipLocale?: boolean
}

export const LocalizedLink = forwardRef<HTMLAnchorElement, LocalizedLinkProps>(
  function LocalizedLink ({ to, skipLocale = false, ...props }, ref) {
    const { locale } = useLocale()

    const localizedTo = useMemo(() => {
      if (skipLocale) {
        return to
      }

      // Handle external URLs
      if (to.startsWith('http://') || to.startsWith('https://') || to.startsWith('//')) {
        return to
      }

      // Handle hash-only links
      if (to.startsWith('#')) {
        return to
      }

      // Strip existing locale and add current one
      const cleanPath = stripLocaleFromPath(to)
      return localizeUrl(cleanPath, locale)
    }, [ to, locale, skipLocale ])

    return <Link ref={ref} to={localizedTo} {...props} />
  },
)
